import { Inject, Injectable, Renderer2, RendererFactory2 } from "@angular/core";
import { Subject } from "rxjs";

import { AzureSearchService, AzureSearchRequest, AzureSearchResult } from "../../azure-search/azure-search";
import { InventoryItem } from "../../inventory/inventory";

import { ActivatedRoute, Params, Router } from "@angular/router";
import { MapBounds } from "../models/facets/map-facet.model";
import { SearchRequest } from "../models/search-request.model";
import { LocationFacet } from "../models/facets/location-facet.model";
import { IpGeoLocationService, IpInfo, LocalStorageService, Location, LogService, SeoRouteData, SeoService } from "../../core/core";
import { CurrencyPipe, DOCUMENT } from "@angular/common";

// if we change pagesize change the search-results-griod-layout pagination
const PageSize = 17;
const IndexName = "machines";

@Injectable({
  providedIn: "root",
})
export class SearchResultsService {
  private azureSearchRequest = new AzureSearchRequest();
  currentPageSize = PageSize;
  private totalInventoryItems;

  public readonly searchRequest$ = new Subject<SearchRequest>();
  searchRequest = new SearchRequest();

  public readonly searchResults$ = new Subject<AzureSearchResult<InventoryItem>>();
  private searchResults = new AzureSearchResult<InventoryItem>();

  mapSearch: boolean = false;

  userAllowedLocation: boolean = false;
  ipInfo: IpInfo;
  renderer: Renderer2;

  // Cache full unfiltered facets for primaryType to show all types even when filtered
  private cachedPrimaryTypeFacets: any[] = null;
  // Track the last searched location to only clear cache when location changes
  private lastSearchedLocationKey: string = null;

  // if we want to reduce size of response. needs more testing
  // defaultSelectFields: string =
  //   "thumbnails,year,hours,usage,usageLabel,usageAbbreviation,enginePower,isTopOwner, purchasePrice, createdDateTime,status,make,model,primaryType,weight,rentalRate,typeDefinition,displayName,description,relatedAttachments,location,rateSchedules,account";

  constructor(
    private azureSearchService: AzureSearchService,
    private router: Router,
    private route: ActivatedRoute,
    private seoService: SeoService,
    private logService: LogService,
    private localStorageService: LocalStorageService,
    private ipGeoLocationService: IpGeoLocationService,
    private currencyPipe: CurrencyPipe,
    @Inject(DOCUMENT) private document: Document,
    rendererFactory: RendererFactory2,
  ) {
    this.azureSearchRequest.top = PageSize;
    this.azureSearchRequest.facets = [
      "make, count:500, sort:count",
      "model, count:500, sort:count",
      "primaryType, count: 500, sort:count",
      "typeDefinition, count: 500, sort:count",
      "rpoEnabled",
      "buyItNowEnabled",
      "isTopOwner",
      "weight,interval:1",
      "rentalRate,interval:5",
      "hours,interval:100",
      "year, interval:1",
      "enginePower, interval: 1",
      "relatedAttachments/size, count: 500, sort:value",
      "relatedAttachments/typeDefinition, count: 400, sort:value",
    ];
    this.azureSearchRequest.searchFields = "make,model,primaryType,typeDefinition,displayName,relatedAttachments/name";
    this.azureSearchRequest.searchMode = "all";
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  search(searchTerm: string, location: LocationFacet, primaryType: string, top: number = 17, newSearch: boolean = false, selectFields: string = null) {
    // Compute the location key to detect if location changed
    const newLocationKey = location.location ? `${location.location.address?.city}-${location.location.address?.stateProvince}` : null;
    const locationChanged = this.lastSearchedLocationKey !== null && this.lastSearchedLocationKey !== newLocationKey;

    this.clearSearchResults(locationChanged);

    if (!location.location) {
      const userLocationIp = this.localStorageService.get("user-location-ip", true);
      if (userLocationIp) {
        this.ipInfo = userLocationIp;
        location.location = new Location();
        location.location.address.city = userLocationIp.city;
        location.location.address.stateProvince = this.ipGeoLocationService.getStateCode(userLocationIp.region);
        location.location.longitude = +this.ipInfo.loc.split(",")[1];
        location.location.latitude = +this.ipInfo.loc.split(",")[0];
        this.continueSearch(searchTerm, location, primaryType, top, newSearch, selectFields);
        return;
      }

      this.ipGeoLocationService.getUserLocation().subscribe((ipInfo: IpInfo) => {
        this.ipInfo = ipInfo;
        location.location = new Location();
        location.location.address.city = ipInfo.city;
        location.location.address.stateProvince = this.ipGeoLocationService.getStateCode(ipInfo.region);
        location.location.longitude = +this.ipInfo.loc.split(",")[1];
        location.location.latitude = +this.ipInfo.loc.split(",")[0];
        this.localStorageService.set("user-location-ip", ipInfo, true);
        this.continueSearch(searchTerm, location, primaryType, top, newSearch, selectFields);
      });
      return;
    }

    this.continueSearch(searchTerm, location, primaryType, top, newSearch, selectFields);
  }

  continueSearch(searchTerm: string, location: LocationFacet, primaryType: string, top: number = 17, newSearch: boolean = false, selectFields: string = null) {
    this.searchRequest.searchTerm = searchTerm;
    this.searchRequest.location = location;
    this.azureSearchRequest.select = selectFields;

    // Update the last searched location key
    this.lastSearchedLocationKey = location.location ? `${location.location.address?.city}-${location.location.address?.stateProvince}` : null;
    if (searchTerm == null) {
      searchTerm = "";
      this.searchRequest.searchTerm = "";
    }

    this.azureSearchRequest.search = searchTerm ? `${searchTerm} | ${searchTerm}* | "${searchTerm}"` : "";

    // For map searches, if we don't have cached facets and primaryType is selected, do a pre-search to cache all types
    if (top === 1000 && !this.cachedPrimaryTypeFacets && primaryType && primaryType !== "all") {
      this.prefetchPrimaryTypeFacets(location);
    }

    // if it is a new search, such as a user going straight to the search results page, or doing a search from the page, just filter for status equal available
    // For map searches (top = 1000), don't exclude primaryType/make from newsearchFilter
    const excludePrimaryTypeAndMake = newSearch && top !== 1000;
    this.azureSearchRequest.filter = newSearch ? this.searchRequest.newsearchFilter(excludePrimaryTypeAndMake) : this.searchRequest.filter();

    this.azureSearchRequest.orderby = this.searchRequest.order;
    if (this.searchRequest.order.includes("buyItNowPrice")) {
      this.azureSearchRequest.filter = this.azureSearchRequest.filter + " and buyItNowEnabled eq true";
    }
    // When sorting by rentalRate, exclude OnRent/Reserved items AND buy-it-now-only items as they're not available for rental
    if (this.searchRequest.order.includes("rentalRate")) {
      this.azureSearchRequest.filter = this.azureSearchRequest.filter + " and (status eq 'Available' or status eq 'Onboarding') and buyItNowOnly ne true";
    }
    this.azureSearchRequest.skip = this.searchRequest.skip;
    this.azureSearchRequest.top = top;
    this.mapSearch = top == 1000; // if we have a top of a 1000 that means we have a map search

    this.azureSearchService.search<InventoryItem>(this.azureSearchRequest, IndexName).subscribe((result: AzureSearchResult<InventoryItem>) => {
      // Cache primaryType facets on the FIRST unfiltered search (when we get all types back)
      if (!this.cachedPrimaryTypeFacets && result.search.facets?.primaryType && result.search.facets.primaryType.length > 1) {
        this.cachedPrimaryTypeFacets = [...result.search.facets.primaryType];
      }

      // Always use cached facets if available (so all types show even when filtered)
      if (this.cachedPrimaryTypeFacets && result.search.facets) {
        result.search.facets.primaryType = this.cachedPrimaryTypeFacets;
      }

      this.searchRequest.initializeFacets(result.search.facets);
      this.azureSearchRequest.filter = this.searchRequest.filter();

      if (!newSearch || this.searchRequest.facetSelectedCountForSearching === 0) {
        this.searchFinished(result);
        return;
      }

      // the reason for this logic is because azure search index only includes facets for your search results, and not for the whole result set
      if (this.searchRequest.primaryType.selected) {
        this.primaryTypeSelected();
      } else if (this.searchRequest.make.selected) {
        this.makeSelected();
      } else {
        this.lastSearch();
      }
    });
  }

  // might be able to remove this shit
  primaryTypeSelected() {
    if (!this.searchRequest.make.makeSelected) {
      this.searchRequest.make?.clear();
    }
    this.searchRequest?.attachment?.clear(); // always clear attachment
    this.searchRequest?.model?.clear(); // always clear model
    this.azureSearchRequest.filter = this.searchRequest.filter(true);

    this.azureSearchService.search<InventoryItem>(this.azureSearchRequest, IndexName).subscribe((result: AzureSearchResult<InventoryItem>) => {
      // Restore full primaryType facets if cached
      if (this.cachedPrimaryTypeFacets && result.search.facets) {
        result.search.facets.primaryType = this.cachedPrimaryTypeFacets;
      }

      this.searchRequest.initializeFacets(result.search.facets);
      this.searchRequest.make.update(result.search.facets.make);
      this.azureSearchRequest.filter = this.searchRequest.filter();

      // if make is selected we need to wipe out model in the
      if (this.searchRequest.make.makeSelected) {
        this.makeSelected();
        // if model is selected or type def selected we need to make one more god damn search
      } else if (this.searchRequest.model.selected || this.searchRequest.typeDefinition.selected) {
        this.lastSearch();
      } else {
        this.searchFinished(result);
      }
    });
  }

  makeSelected() {
    this.searchRequest.attachment.clear(); // always clear attachment

    // if the model is selected we need to wipe out and re apply the filter
    if (this.searchRequest.model.selected) {
      this.searchRequest.model.clear();
      this.azureSearchRequest.filter = this.searchRequest.filter();
    }

    this.azureSearchService.search<InventoryItem>(this.azureSearchRequest, IndexName).subscribe((result: AzureSearchResult<InventoryItem>) => {
      // Restore full primaryType facets if cached
      if (this.cachedPrimaryTypeFacets && result.search.facets) {
        result.search.facets.primaryType = this.cachedPrimaryTypeFacets;
      }

      this.searchRequest.initializeFacets(result.search.facets);
      this.azureSearchRequest.filter = this.searchRequest.filter();

      if (this.searchRequest.model.selected) {
        this.lastSearch();
      } else {
        this.searchFinished(result);
      }
    });
  }

  lastSearch() {
    this.azureSearchService.search<InventoryItem>(this.azureSearchRequest, IndexName).subscribe((result: AzureSearchResult<InventoryItem>) => {
      this.searchFinished(result);
    });
  }

  searchFinished(result: AzureSearchResult<InventoryItem>) {
    this.totalInventoryItems = result.odata.count;
    if (!this.mapSearch) {
      this.modifyResults(result);
      this.searchResults = result;
      this.searchResults$.next(result);
    } else {
      // we are searching the map we need to query for an extra 1000 machines since the azure index only supports 1000 results per request
      this.azureSearchRequest.skip = 1000;
      this.azureSearchService.search<InventoryItem>(this.azureSearchRequest, IndexName).subscribe((secondResult: AzureSearchResult<InventoryItem>) => {
        result.value = [...result.value, ...secondResult.value];

        this.azureSearchRequest.skip = 2000;
        this.azureSearchService.search<InventoryItem>(this.azureSearchRequest, IndexName).subscribe((thirdResult: AzureSearchResult<InventoryItem>) => {
          result.value = [...result.value, ...thirdResult.value];
          this.searchResults = result;
          this.searchResults$.next(result);
        });
      });
    }

    this.updateFacets(result.search.facets);
  }

  modifyResults(result: AzureSearchResult<InventoryItem>) {
    const userLongLat = this.searchRequest.location.getLatLong();
    if (userLongLat) {
      for (let machine of result.value) {
        machine.distanceFromUser = userLongLat
          ? this.calculateDistance(userLongLat[0], userLongLat[1], machine.location.longitude, machine.location.latitude)
          : null;
      }
    }
  }

  calculateDistance(lon1, lat1, lon2, lat2): number {
    const R = 6371; // Radius of the earth in km
    const dLat = this.toRad(lat2 - lat1); // Javascript functions in radians
    const dLon = this.toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const km = R * c; // Distance in km
    return Math.ceil(km * 0.621371); // distance in miles
  }

  toRad(coordinate) {
    return (coordinate * Math.PI) / 180;
  }

  nextPage() {
    if (this.totalInventoryItems < this.azureSearchRequest.skip + this.currentPageSize) {
      return;
    }

    this.azureSearchRequest.skip += this.currentPageSize;
    this.azureSearchService.search<InventoryItem>(this.azureSearchRequest, IndexName).subscribe((result: AzureSearchResult<InventoryItem>) => {
      this.totalInventoryItems = result.odata.count;
      this.modifyResults(result);
      const previousSearchResults = this.searchResults;
      Array.prototype.push.apply(previousSearchResults.value, result.value);
      this.searchResults$.next(previousSearchResults);
    });
  }

  paginate(pageIndex: number) {
    const queryParams: Params = {};
    queryParams["page"] = pageIndex;
    if (pageIndex == 0) {
      queryParams["page"] = undefined;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: "merge",
    });
  }

  filterResults() {
    // update params yo
    let queryParams: Params = this.searchRequest.toQueryParam();
    queryParams["page"] = undefined;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: "merge",
    });
  }

  mapMoved(bounds: MapBounds) {
    this.searchRequest.map.mapBounds = bounds;
    this.azureSearchRequest.top = 1000;
    this.filterResults();
  }

  updateFacets(facets: any) {
    this.searchRequest.initializeFacets(facets);
    this.searchRequest$.next(this.searchRequest);
  }

  prefetchPrimaryTypeFacets(location: LocationFacet) {
    // Quick search to get all primaryType facets for the location
    const prefetchRequest = new AzureSearchRequest();
    prefetchRequest.top = 0; // Don't need results, just facets
    prefetchRequest.facets = ["primaryType, count: 500, sort:count"];
    prefetchRequest.filter = this.searchRequest.initialFilter + ` and ${location.newFilter(location.location)}`;

    this.azureSearchService.search<InventoryItem>(prefetchRequest, IndexName).subscribe((result: AzureSearchResult<InventoryItem>) => {
      if (result.search.facets?.primaryType) {
        this.cachedPrimaryTypeFacets = [...result.search.facets.primaryType];
      }
    });
  }

  clearSearchResults(locationChanged: boolean = false) {
    window.scrollTo(0, 0);
    this.azureSearchRequest.skip = 0;

    this.searchResults = null;
    this.searchResults$.next(null);

    // Only clear cached facets when location actually changes, not when just switching categories
    if (locationChanged) {
      this.cachedPrimaryTypeFacets = null;
    }
  }

  resetSearchRequest() {
    this.searchRequest = new SearchRequest();
    this.searchRequest$.next(null);
    // Clear all cached data on full reset
    this.cachedPrimaryTypeFacets = null;
    this.lastSearchedLocationKey = null;
  }

  updateMetaTags() {
    let searchTerm = this.searchRequest.searchTerm || null;
    // searchTerm = searchTerm[0].toUpperCase() + searchTerm.slice(1);
    const cityStateClean = this.searchRequest.location.toCityState();

    const pathSegments = window.location.pathname.split("/").filter(Boolean);
    const baseRoute = pathSegments[0]; // 'equipment-rentals' or 'equipment-for-sale'
    const equipmentType = pathSegments[1] ? decodeURIComponent(pathSegments[1]) : null;
    const cityState = pathSegments[2] ? decodeURIComponent(pathSegments[2]) : null;
    const make = pathSegments[3] ? decodeURIComponent(pathSegments[3]) : null;
    const lowestRentalRate = this.getLowestDailyRate(this.searchResults.value);
    const lowestRentalRateFormatted = this.currencyPipe.transform(lowestRentalRate, "USD", "symbol", "1.0-0");

    const isForSale = baseRoute === "equipment-for-sale";
    const actionWord = isForSale ? "For Sale" : "Rentals";
    const actionVerb = isForSale ? "Buy" : "Rent";

    let title = "";
    let description = "";

    // city only
    if (equipmentType == "all" && cityState) {
      title = isForSale ? `Construction Equipment For Sale in ${cityStateClean} | Rubbl` : `Construction Equipment Rentals in ${cityStateClean} | Rubbl`;
      description = isForSale
        ? `Search online for heavy equipment for sale in ${cityStateClean}. Find construction and industrial machinery for sale in ${cityStateClean} and nearby surrounding areas.`
        : `Search online for heavy equipment rentals in ${cityStateClean}. Find construction and industrial machinery rentals in ${cityStateClean} and nearby surrounding areas.`;
    }

    // city plus category
    if (equipmentType != "all" && cityState) {
      title = `${equipmentType}s ${actionWord} in ${cityStateClean} - ${this.searchResults.odata.count} Available | Rubbl`;
      description = isForSale
        ? `Find ${this.searchResults.odata.count} ${equipmentType} for sale near ${cityStateClean}. Buy the best ${equipmentType} equipment for your project today. Inventory updated daily.`
        : `Find ${this.searchResults.odata.count} ${equipmentType} rentals near ${cityStateClean}. Rent the best ${equipmentType} equipment for your project today starting at ${lowestRentalRateFormatted}. Inventory updated daily.`;
    }

    // city category make
    if (equipmentType != "all" && cityState && make) {
      title = `${make} ${equipmentType}s ${actionWord} in ${cityStateClean} - ${this.searchResults.odata.count} Available | Rubbl`;
      description = isForSale
        ? `Find ${this.searchResults.odata.count} ${make} ${equipmentType} for sale near ${cityStateClean}. Buy the best ${make} ${equipmentType} equipment for your project today. Inventory updated daily.`
        : `Find ${this.searchResults.odata.count} ${make} ${equipmentType} rentals near ${cityStateClean}. Rent the best ${make} ${equipmentType} equipment for your project today starting at ${lowestRentalRateFormatted}. Inventory updated daily. `;
    }

    // only category
    if (equipmentType != "all" && !cityState) {
      title = `${equipmentType}s ${actionWord} Near Me | Rubbl`;
      description = isForSale
        ? `Find ${equipmentType} for sale near you. Buy the best ${equipmentType} equipment for your project today. Insurance and delivery options available.`
        : `Find ${equipmentType} rentals near you. Rent the best ${equipmentType} equipment for your project today. Insurance, delivery options, and special rates available.`;
    }

    if (equipmentType == "all" && !cityState) {
      title = isForSale ? `Construction Equipment For Sale Near Me | Rubbl` : `Construction Equipment Rentals Near Me | Rubbl`;
      description = isForSale
        ? `Find contruction equipment for sale near you. Buy the best equipment for your project today. Insurance and delivery options available.`
        : `Find contruction equipment rentals near you. Rent the best  equipment for your project today. Insurance, delivery options, and special rates available.`;
    }

    const cityKeyword = cityStateClean ? `${cityStateClean},` : "";
    const actionKeywords = isForSale ? "sale,buy,purchase,for sale,buy it now," : "rental,rent,lease,rto,";
    const searchTermKeyword = searchTerm ? `${searchTerm},` : "";

    this.seoService.setSeo({
      title: title,
      description,
      keywords: `rubbl,${searchTermKeyword}${cityKeyword}heavy,equipment,construction,${actionKeywords}dirt,moving,dozer,excavator,caterpillar,near,me`,
      image: "https://company.rubbl.com/pubweb/marketplace/images/logos/og-rubbl-dark.jpg",
    } as SeoRouteData);

    this.setCanonicalURL();
    this.updateNoIndexMetaTag(this.searchResults.value);
    this.set404MetaTags();
    window["prerenderReady"] = true;
  }

  set404MetaTags() {
    const head = document.head;
    const existingMeta = document.querySelector('meta[name="prerender-status-code"]');

    if (this.searchResults?.odata?.count === 0) {
      if (!existingMeta) {
        const meta = document.createElement("meta");
        meta.name = "prerender-status-code";
        meta.content = "404";
        head.appendChild(meta);
      } else {
        existingMeta.setAttribute("content", "404");
      }
    } else {
      if (existingMeta) {
        head.removeChild(existingMeta);
      }
    }
  }

  setCanonicalURL() {
    const url = window.location.origin + window.location.pathname;
    let link: HTMLLinkElement = this.document.querySelector("link[rel='canonical']");
    if (link) {
      this.renderer.setAttribute(link, "href", url);
    } else {
      link = this.renderer.createElement("link");
      this.renderer.setAttribute(link, "rel", "canonical");
      this.renderer.setAttribute(link, "href", url);
      this.renderer.appendChild(this.document.head, link);
    }
  }

  updateNoIndexMetaTag(results: any[]) {
    const head = document.head;
    const existingMeta = document.querySelector('meta[name="robots"]');

    if (results.length === 0) {
      if (!existingMeta) {
        const meta = document.createElement("meta");
        meta.name = "robots";
        meta.content = "noindex";
        head.appendChild(meta);
      } else {
        existingMeta.setAttribute("content", "noindex");
      }
    } else {
      if (existingMeta && existingMeta.getAttribute("content") === "noindex") {
        head.removeChild(existingMeta);
      }
    }
  }

  getLowestDailyRate(machines: any[]): number | null {
    const dayRates: number[] = [];

    for (const machine of machines) {
      if (!machine.rateSchedules) continue;

      for (const rate of machine.rateSchedules) {
        if (rate.label === "DAY" && rate.cost !== undefined && rate.cost !== null) {
          dayRates.push(rate.cost);
        }
      }
    }

    return dayRates.length > 0 ? Math.min(...dayRates) : null;
  }

  storeLocation(location: Location) {
    if (location && location.address?.city && location.address?.stateProvince) {
      this.localStorageService.set(`location-${location.address.city.replace(" ", "-")}-${location.address.stateProvince}`, location);
      this.localStorageService.set(`current-location`, location);
    }
  }
}
